import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
  OriginBadge,
} from "@/components/admin/shared/PageHeader";
import {
  PennField,
  PennInput,
  PennTextarea,
  PennDropdown,
  PennCheckbox,
  PennText,
  PennLabel,
} from "@/components/ui";
import {
  useRoles,
  useUpdateRole,
  useCreateRole,
  useDeactivateRole,
} from "@/hooks/useUsers";
import type {
  AdminRole,
  PermissionKey,
  UpdateRoleBody,
  CreateRoleBody,
} from "@/api/roles";

/* ── Types ─────────────────────────────────────────────────────── */

// The 9 real permission booleans (rule §D.1) — replaces the earlier mock list.
const PERMISSION_OPTIONS: { key: PermissionKey; label: string }[] = [
  { key: "canCreateApplications", label: "Can create applications" },
  { key: "canWorkForOthers", label: "Can work for others" },
  { key: "canSeePrices", label: "Can see prices" },
  { key: "canGenerateQuote", label: "Can generate quote" },
  { key: "canGenerateRoi", label: "Can generate ROI" },
  { key: "canApproveEngineering", label: "Can approve engineering" },
  { key: "canSwitchRegion", label: "Can switch region" },
  { key: "canManageUsers", label: `Can manage users (Admin) ` },
  { key: "canManageMasterData", label: `Can manage master data  (Admin)` },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const roleSchema = z.object({
  name: z
    .string()
    .min(1, "Role name is required")
    .max(50, "Role name must be 50 characters or fewer"),
  description: z.string().optional(),
  permissions: z.array(z.string()),
  status: z.enum(["Active", "Inactive"]),
  // Staff classification (ADR-0021) — create-only; the API rejects it on PATCH.
  isInternal: z.boolean(),
});

type RoleFormData = z.infer<typeof roleSchema>;

type Role = RoleFormData & {
  id: number;
  code: string;
  usersAssigned: number;
  createdAt: string;
};

// The 5 §D.1 seed roles are permanent — no delete/deactivate (custom roles
// only). Mirrors the svc-03 seed guard.
const CANONICAL_ROLE_CODES: ReadonlySet<string> = new Set([
  "admin",
  "sales",
  "engineering",
  "external_customer",
  "distributor_partner",
]);

// The two canonical external seeds are assignable to BOTH user types (they are
// external roles, but internal staff may be demoted onto them) — the Type
// column shows both badges for them.
const DUAL_TYPE_ROLE_CODES: ReadonlySet<string> = new Set([
  "external_customer",
  "distributor_partner",
]);

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; role?: Role } | null;

// New custom roles are ALWAYS internal (staff) — the checkbox is fixed and
// non-editable in the create form (operator call 2026-07-17).
const blank: RoleFormData = {
  name: "",
  description: "",
  permissions: [],
  status: "Active",
  isInternal: true,
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (
      err as { response?: { data?: { error?: { message?: string } } } }
    ).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

// Map the API role to the page's row shape.
const toRow = (r: AdminRole): Role => ({
  id: r.id,
  code: r.code,
  name: r.displayName,
  description: r.description ?? "",
  isInternal: r.isInternal,
  permissions: PERMISSION_OPTIONS.filter((o) => r[o.key]).map((o) => o.key),
  status: r.isActive ? "Active" : "Inactive",
  usersAssigned: r.usersAssigned,
  createdAt: r.createdAt
    ? new Date(r.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—",
});

/* ── Sub-components ─────────────────────────────────────────────── */

const StatusBadge = ({ status }: { status: "Active" | "Inactive" }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-0.5 rounded-full text-small-medium border ${
      status === "Active"
        ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-800"
        : "bg-gray-100 text-blue_grey3 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${status === "Active" ? "bg-green-500" : "bg-gray-400"}`}
    />
    {status}
  </span>
);

/* ── Page ───────────────────────────────────────────────────────── */

export const RolesPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useRoles();
  const updateRoleMutation = useUpdateRole();
  const createRoleMutation = useCreateRole();
  const deactivateRoleMutation = useDeactivateRole();
  const roles = useMemo<Role[]>(() => (data ?? []).map(toRow), [data]);

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const isView = modal?.mode === "view";
  const isEdit = modal?.mode === "edit";
  // The two external seed roles (External/Customer, Distributor/Partner) have
  // a FIXED permission set — they carry only "Can create applications" and
  // must not be handed staff permissions (operator call 2026-07-17). Their
  // permission checkboxes are read-only even in edit mode.
  const permissionsLocked =
    isView ||
    (isEdit && !!modal?.role && DUAL_TYPE_ROLE_CODES.has(modal.role.code));

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: blank,
  });

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (role: Role) => {
    setFormError(null);
    reset(role);
    setModal({ mode: "edit", role });
  };
  const openView = (role: Role) => {
    setFormError(null);
    reset(role);
    setModal({ mode: "view", role });
  };

  const onSubmit = (formData: RoleFormData) => {
    if (isEdit && modal?.role) {
      setFormError(null);
      const perms = {} as Record<PermissionKey, boolean>;
      for (const o of PERMISSION_OPTIONS)
        perms[o.key] = formData.permissions.includes(o.key);
      const body: UpdateRoleBody = {
        displayName: formData.name.trim(),
        description: formData.description?.trim() || null,
        isActive: formData.status === "Active",
        // Locked roles (external seeds): omit the permission booleans entirely
        // so the PATCH cannot alter their fixed matrix.
        ...(permissionsLocked ? {} : perms),
      };
      updateRoleMutation.mutate(
        { id: modal.role.id, body },
        {
          onSuccess: () => setModal(null),
          onError: (err) =>
            setFormError(apiErrorMessage(err, "Failed to update role.")),
        },
      );
    } else {
      // Create a custom role (ADR-0011). Status is omitted — new roles start active.
      setFormError(null);
      const perms = {} as Record<PermissionKey, boolean>;
      for (const o of PERMISSION_OPTIONS)
        perms[o.key] = formData.permissions.includes(o.key);
      const body: CreateRoleBody = {
        displayName: formData.name.trim(),
        description: formData.description?.trim() || null,
        // Fixed: admin-created custom roles are always internal (staff).
        isInternal: true,
        ...perms,
      };
      createRoleMutation.mutate(body, {
        onSuccess: () => setModal(null),
        onError: (err) =>
          setFormError(apiErrorMessage(err, "Failed to create role.")),
      });
    }
  };

  const cols: ColDef<Role>[] = [
    {
      header: "Role Name",
      render: (r) => (
        <div>
          <p className="text-blue_grey text-body-medium">
            {r.name}
          </p>
          {r.description && (
            <p className="text-blue_grey4 mt-1 truncate max-w-xs text-small">
              {r.description}
            </p>
          )}
        </div>
      ),
    },
    {
      header: "Users Assigned",
      render: (r) => (
        <button
          type="button"
          onClick={() =>
            navigate(`/admin/users?role=${encodeURIComponent(r.code)}`)
          }
          title={`Show users with the ${r.name} role`}
          aria-label={`Show users with the ${r.name} role`}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-penn-50 dark:bg-penn-900/40 text-penn-500 dark:text-penn-400 border border-penn-100 dark:border-penn-700 cursor-pointer hover:bg-penn-100 dark:hover:bg-penn-900/60 hover:border-penn-400 dark:hover:border-penn-700 transition-colors duration-150 text-small-medium"
        >
          {r.usersAssigned}
        </button>
      ),
    },
    // {
    //   header: "Permissions",
    //   render: (r) => <PermissionChips permissions={r.permissions} />,
    // },
    {
      header: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      header: "Type",
      render: (r) =>
        DUAL_TYPE_ROLE_CODES.has(r.code) ? (
          <div className="flex items-center gap-2">
            <OriginBadge isInternal={true} />
            <OriginBadge isInternal={false} />
          </div>
        ) : (
          <OriginBadge isInternal={r.isInternal} />
        ),
    },
    {
      header: "Created AT",
      render: (r) => (
        <span className="text-blue_grey3 text-small">
          {r.createdAt}
        </span>
      ),
    },
    {
      header: "Actions",
      render: (r) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(r)} />
          <EditButton onClick={() => openEdit(r)} />
          <DeleteButton
            onClick={() => setDeleteTarget(r)}
            disabled={CANONICAL_ROLE_CODES.has(r.code)}
            disabledTitle="Built-in role — cannot be deleted"
          />
        </div>
      ),
    },
  ];

  const modalTitleMap = {
    add: "Create New Role",
    edit: "Edit Role",
    view: "View Role",
  };
  const modalTitle = modal ? modalTitleMap[modal.mode] : "";

  return (
    <>
      <PageHeader
        title="Roles Management"
        subtitle="Create, configure, and manage user roles and permissions."
        action={<AddButton onClick={openAdd} label="Add Role" />}
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-red-700 dark:text-red-400 text-body">
          Failed to load roles
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <AdminTable
        columns={cols}
        rows={roles}
        keyExtractor={(r) => r.id}
        loading={isLoading}
      />

      {/* Create / Edit / View Modal */}
      <AdminModal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modalTitle}
        maxWidth="max-w-md"
        footer={
          isView ? (
            <CancelButton onClick={() => setModal(null)} />
          ) : (
            <>
              <CancelButton onClick={() => setModal(null)} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={
                  isEdit
                    ? updateRoleMutation.isPending
                      ? "Saving…"
                      : "Save changes"
                    : createRoleMutation.isPending
                      ? "Creating…"
                      : "Create Role"
                }
              />
            </>
          )
        }
      >
        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-red-700 dark:text-red-400 text-body">
              {formError}
            </div>
          )}

          {/* Modal subtitle */}
          <PennText as="p" size="sm" color="blue_grey" className="-mt-1">
            {isView
              ? "Role details and access permissions."
              : isEdit
                ? "Update the role name, description, and permissions."
                : "Define a new role and set its access permissions."}
          </PennText>

          {/* Role Name */}
          <PennField
            label="Role Name"
            htmlFor="roleName"
            required={!isView}
            error={!isView ? errors.name?.message : undefined}
          >
            <PennInput
              id="roleName"
              disabled={isView}
              maxLength={50}
              error={!isView && !!errors.name}
              placeholder="e.g. Role Name"
              {...register("name")}
            />
          </PennField>

          {/* Description */}
          <PennField
            label="Description"
            htmlFor="roleDesc"
            error={!isView ? errors.description?.message : undefined}
          >
            <PennTextarea
              id="roleDesc"
              disabled={isView}
              rows={3}
              placeholder="Brief description of this role's responsibilities and access level..."
              {...register("description")}
            />
          </PennField>

          {/* Internal (staff) classification — read-only in every mode. New
              custom roles are always created internal (fixed, operator call
              2026-07-17); on existing roles the API rejects it on PATCH
              (flipping a live role would desync already-assigned users'
              is_internal / Cognito group), so edit/view show the stored value. */}
          <Controller
            name="isInternal"
            control={control}
            render={({ field }) => (
              <label className="flex items-start gap-3 select-none rounded-lg border p-4 cursor-not-allowed opacity-70 bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-body">
                <PennCheckbox
                  disabled
                  checked={field.value}
                  onChange={() => {}}
                />
                <span>
                  <span className="block text-blue_grey font-medium">
                    Internal (staff) role
                  </span>
                  <span className="block text-blue_grey4 mt-1 text-small">
                    Members sign in via the internal (admin) app. External
                    (customer) users can never be assigned an internal role.
                    Every role created here is an internal role — this cannot be
                    changed.
                  </span>
                </span>
              </label>
            )}
          />

          {/* Status — hidden in add mode, shown in edit/view */}
          {modal?.mode !== "add" && (
            <PennField label="Status" htmlFor="roleStatus">
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <PennDropdown
                    inputId="roleStatus"
                    name={field.name}
                    options={STATUS_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                  />
                )}
              />
            </PennField>
          )}

          {/* Access Permissions */}
          <div>
            <PennLabel
              htmlFor=""
              size="sm"
              font="medium"
              color="blue_grey1"
              className="block mb-3 uppercase text-label"
            >
              Access Permissions
            </PennLabel>
            <Controller
              name="permissions"
              control={control}
              render={({ field }) => (
                <div
                  className={`space-y-3 rounded-lg border p-4 ${permissionsLocked ? "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700" : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800"}`}
                >
                  {PERMISSION_OPTIONS.map((opt) => {
                    const checked = field.value.includes(opt.key);
                    return (
                      <label
                        key={opt.key}
                        className={`flex items-center gap-3 text-body select-none ${permissionsLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                      >
                        <PennCheckbox
                          disabled={permissionsLocked}
                          checked={checked}
                          onChange={() => {
                            if (permissionsLocked) return;
                            const next = checked
                              ? field.value.filter((v: string) => v !== opt.key)
                              : [...field.value, opt.key];
                            field.onChange(next);
                          }}
                        />
                        <span className="text-blue_grey">
                          {opt.label}
                        </span>
                      </label>
                    );
                  })}
                  {isEdit && permissionsLocked && (
                    <p className="pt-1 text-blue_grey4 text-small">
                      This built-in external role's permissions are fixed and
                      cannot be edited.
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </form>
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deactivateRoleMutation.mutate(deleteTarget.id, {
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.name}
      />
    </>
  );
};
